import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  useContext,
  useEffect,
  useReducer
} from 'react';
import { getColor, getItemFromStore } from 'helpers/utils';
import {
  Config,
  initialConfig,
  Company,
  User,
  UserModule,
  AuthData
} from 'config';
import {
  ACTIONTYPE,
  configReducer,
  SET_CONFIG,
  SET_COMPANY_LOADING,
  SET_COMPANY_SUCCESS,
  SET_COMPANY_ERROR,
  SET_AUTH_LOADING,
  SET_AUTH_SUCCESS,
  SET_AUTH_ERROR,
  LOGOUT
} from 'reducers/ConfigReducer';
import axios, { AxiosError } from 'axios';

interface AppContextInterFace {
  config: Config;
  configDispatch: Dispatch<ACTIONTYPE>;
  toggleTheme: () => void;
  setConfig: (payload: Partial<Config>) => void;
  getThemeColor: (name: string) => string;
  // Company methods
  getCurrentCompany: () => Company | null;
  isCompanyLoading: () => boolean;
  getCompanyError: () => string | null;
  reloadCompanyData: () => Promise<void>;
  // Auth methods
  getCurrentUser: () => User | null;
  getUserModules: () => UserModule[];
  isAuthenticated: () => Promise<boolean> | false;
  isAuthLoading: () => boolean;
  getAuthError: () => string | null;
  login: (
    username: string,
    password: string,
    companyID: number
  ) => Promise<void>;
  loginWithPin: (pin: number, companyID: number) => Promise<void>;
  logout: () => void;
  hasModulePermission: (
    moduleId: number,
    requiredPermission?: number
  ) => boolean;
}

export const AppContext = createContext({} as AppContextInterFace);

const apiClient = axios.create({
  baseURL: 'https://wsdigital.ctcp.pt/loginQueries',
  // baseURL: 'http://localhost:3400/loginQueries',
  withCredentials: true,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

const AppProvider = ({ children }: PropsWithChildren) => {
  const configState: Config = {
    isNavbarVerticalCollapsed: getItemFromStore(
      'isNavbarVerticalCollapsed',
      initialConfig.isNavbarVerticalCollapsed
    ),
    openNavbarVertical: initialConfig.openNavbarVertical,
    theme: getItemFromStore('theme', initialConfig.theme),
    navbarTopAppearance: getItemFromStore(
      'navbarTopAppearance',
      initialConfig.navbarTopAppearance
    ),
    navbarVerticalAppearance: getItemFromStore(
      'navbarVerticalAppearance',
      initialConfig.navbarVerticalAppearance
    ),
    navbarPosition: getItemFromStore(
      'navbarPosition',
      initialConfig.navbarPosition
    ),
    navbarTopShape: getItemFromStore(
      'navbarTopShape',
      initialConfig.navbarTopShape
    ),
    isRTL: getItemFromStore('isRTL', initialConfig.isRTL),
    isDark: getItemFromStore('isDark', initialConfig.isDark),
    isChatWidgetVisible: getItemFromStore(
      'isChatWidgetVisible',
      initialConfig.isChatWidgetVisible
    ),
    companyState: initialConfig.companyState,
    authState: initialConfig.authState
  };

  const [config, configDispatch] = useReducer(configReducer, configState); // initail

  const setConfig = (payload: Partial<Config>) => {
    configDispatch({
      type: SET_CONFIG,
      payload
    });
  };

  const toggleTheme = () => {
    configDispatch({
      type: SET_CONFIG,
      payload: {
        theme: config.isDark ? 'light' : 'dark'
      }
    });
  };

  const getThemeColor = (name: string) => {
    return getColor(name);
  };

  // Company-related functions
  const fetchCompanyByDomain = async (domain: string): Promise<Company> => {
    try {
      const response = await apiClient.post('/company', {
        domain: domain
      }); // if i add withCredentials it causes cors problems

      return {
        id_entidade: response.data[0].id_entidade,
        name: response.data[0].name || 'Unknown Company',
        logo: response.data[0].logo || '',
        modulo1: response.data[0].modulo1 || 'n',
        modulo2: response.data[0].modulo2 || 'n',
        modulo3: response.data[0].modulo3 || 'n',
        modulo4: response.data[0].modulo4 || 'n',
        modulo5: response.data[0].modulo5 || 'n',
        modulo6: response.data[0].modulo6 || 'n'
      };
    } catch (error) {
      throw new Error('Failed to fetch company information');
    }
  };

  const loadCompanyData = async () => {
    try {
      configDispatch({ type: SET_COMPANY_LOADING });
      const currentDomain = window.location.host;
      const companyData = await fetchCompanyByDomain(currentDomain);
      configDispatch({ type: SET_COMPANY_SUCCESS, payload: companyData });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';
      configDispatch({ type: SET_COMPANY_ERROR, payload: errorMessage });
    }
  };

  const getCurrentCompany = () => config.companyState.company;
  const isCompanyLoading = () => config.companyState.isLoading;
  const getCompanyError = () => config.companyState.error;
  const reloadCompanyData = () => loadCompanyData();

  // Auth-related functions
  const loginUser = async (
    email: string,
    password: string,
    companyID: number
  ) => {
    try {
      configDispatch({ type: SET_AUTH_LOADING });

      const response = await apiClient.post('/login', {
        email,
        password,
        companyID
      });

      // Transform the response data to match your structure
      const authData: AuthData = {
        user: {
          id_user: response.data.results[0].iduser,
          name: response.data.results[0].name
        },
        modules: response.data.modules,
        isAuthenticated: true
      };

      // Set expiration for 10 hours from now
      const expirationTime = Date.now() + 10 * 60 * 60 * 1000;
      //test
      // const expirationTime = Date.now() + 20 * 1000;
      const loginPermance = {
        expires: expirationTime
      };
      localStorage.setItem('isAuth', JSON.stringify(loginPermance));

      configDispatch({ type: SET_AUTH_SUCCESS, payload: authData });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Login failed';
      configDispatch({ type: SET_AUTH_ERROR, payload: errorMessage });
      throw error;
    }
  };

  const loginPin = async (pin: number, companyID: number) => {
    try {
      configDispatch({ type: SET_AUTH_LOADING });

      const response = await apiClient.post(
        '/reLogPin',
        {
          pin,
          companyID
        },
        { withCredentials: true }
      );

      // Transform the response data to match your structure
      const authData: AuthData = {
        user: {
          id_user: response.data.results[0].iduser,
          name: response.data.results[0].name
        },
        modules: response.data.modules,
        isAuthenticated: true
      };

      // Set expiration for 10 hours from now
      const expirationTime = Date.now() + 10 * 60 * 60 * 1000;
      //test
      // const expirationTime = Date.now() + 20 * 1000;
      const loginPermance = {
        expires: expirationTime
      };
      localStorage.setItem('isAuth', JSON.stringify(loginPermance));

      configDispatch({ type: SET_AUTH_SUCCESS, payload: authData });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Login failed';
      configDispatch({ type: SET_AUTH_ERROR, payload: errorMessage });
      throw error;
    }
  };

  const logoutUser = () => {
    configDispatch({ type: LOGOUT });
  };

  const getBasicInfo = async () => {
    try {
      const response = await apiClient.get('/Authenticated', {
        withCredentials: true
      });

      const authData: AuthData = {
        user: {
          id_user: response.data.results[0].iduser,
          name: response.data.results[0].name
        },
        modules: response.data.modules,
        isAuthenticated: true
      };

      configDispatch({ type: SET_AUTH_SUCCESS, payload: authData });
      return { success: true, status: 'authenticated' };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        localStorage.removeItem('isAuth');
        localStorage.removeItem('rememeberMe');
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('isAuth');
          const errorMessage =
            error instanceof Error ? axiosError.message : 'Login failed';
          configDispatch({ type: SET_AUTH_ERROR, payload: errorMessage });
          return { success: false, status: 'expired' };
        } else if (axiosError.response?.status === 403) {
          // Forbidden
          return { success: false, status: 'forbidden' };
        } else {
          // Network or other errors
          return { success: false, status: 'error' };
        }
      }
    }
  };

  const isAuthenticated = async () => {
    if (config.authState.authData.isAuthenticated) {
      return true;
    }

    const stored = localStorage.getItem('isAuth');
    if (!stored) {
      localStorage.removeItem('isAuth');
      localStorage.removeItem('rememeberMe');
      return false;
    }

    try {
      const { expires } = JSON.parse(stored);
      if (expires ? Date.now() < parseInt(expires) : false) {
        const authResult = await getBasicInfo();
        // await getBasicInfo();
        if (authResult?.success) {
          return true;
        } else {
          // Handle different failure scenarios
          localStorage.removeItem('isAuth');
          localStorage.removeItem('rememeberMe');
          switch (authResult?.status) {
            case 'expired':
              return false;
            case 'forbidden':
              return false;
            default:
              return false;
          }
        }
      }
      localStorage.removeItem('isAuth');
      localStorage.removeItem('rememeberMe');
      return false;
    } catch (error) {
      localStorage.removeItem('isAuth');
      localStorage.removeItem('rememeberMe');
      return false;
    }
  };

  const getCurrentUser = () => config.authState.authData.user;
  const getUserModules = () => config.authState.authData.modules;
  const isAuthLoading = () => config.authState.isLoading;
  const getAuthError = () => config.authState.error;

  const hasModulePermission = (
    moduleId: number,
    requiredPermission?: number
  ) => {
    const userModules = getUserModules();
    const moduleAccess = userModules.find(
      module => module.id_modulo === moduleId
    );

    if (!moduleAccess) return false;

    if (requiredPermission !== undefined) {
      return moduleAccess.id_permissao >= requiredPermission;
    }

    return true; // Has access to module
  };

  // Load company data on mount
  useEffect(() => {
    loadCompanyData();
  }, []);

  useEffect(() => {
    if (config.navbarTopShape === 'slim') {
      // document.body.classList.add('nav-slim');
      document.documentElement.setAttribute(
        'data-navbar-horizontal-shape',
        'slim'
      );
    } else {
      document.documentElement.removeAttribute('data-navbar-horizontal-shape');
    }

    if (config.navbarPosition === 'dual') {
      setConfig({
        navbarTopShape: 'default'
      });
    }

    document.documentElement.setAttribute(
      'data-navigation-type',
      config.navbarPosition
    );

    if (config.isNavbarVerticalCollapsed) {
      document.documentElement.classList.add('navbar-vertical-collapsed');
    } else {
      document.documentElement.classList.remove('navbar-vertical-collapsed');
    }
  }, [config]);

  return (
    <AppContext.Provider
      value={{
        config,
        setConfig,
        toggleTheme,
        getThemeColor,
        configDispatch,
        getCurrentCompany,
        isCompanyLoading,
        getCompanyError,
        reloadCompanyData,
        login: loginUser,
        loginWithPin: loginPin,
        logout: logoutUser,
        isAuthenticated,
        getCurrentUser,
        getUserModules,
        isAuthLoading,
        getAuthError,
        hasModulePermission
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);

export default AppProvider;
